import { useState, useRef } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Snackbar } from "../Snackbar";
import { DEFAULT_SNACKBAR, SUCCESS_PROFILE_PICTURE, ERROR_PROFILE_PICTURE } from "../../utils/snackbar";
import avatarPlaceholder from "../Sidebar/assets/person-circle.svg";
import { Camera } from "lucide-react";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

export const UserProfileModal = ({ open, onClose }) => {
  const { user, UpdateProfilePicture, lang } = useAuth();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState(DEFAULT_SNACKBAR);

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const profilePictureResponse = await UpdateProfilePicture(file);
        if (profilePictureResponse.success) {
          setSnackbar(SUCCESS_PROFILE_PICTURE);
          setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 2000);
        } else {
          setSnackbar(ERROR_PROFILE_PICTURE);
          setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 2000);
        }
      } catch (error) {
        console.error("Failed to update profile picture", error);
        setSnackbar(ERROR_PROFILE_PICTURE);
        setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 2000);
      } finally {
        onClose();
        setUploading(false);
      }
    }
  };

  if (!user) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: "16px",
            backgroundColor: theme.colors.backgroundColorNavBar,
            color: theme.colors.textColorNavbar,
            padding: "2rem",
            zIndex: "1000",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.colors.textColorNavbar,
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: 30 }} />
        </IconButton>
        <DialogContent sx={{ padding: 0 }}>
          <div className="flex flex-col gap-12 max-w-full">
            {/* Header Section: Avatar and Basic Info */}
            <section className="flex flex-col gap-6">
              <div className="relative w-[96px] tablet:w-[128px] h-[96px] tablet:h-[128px]">
                {/* Indicator desktop */}
                <div className="group relative cursor-pointer overflow-hidden rounded-full w-full h-full" onClick={handleAvatarClick}>
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center bg-gray-500/50 opacity-0 backdrop-blur-[1px] transition-opacity duration-300 group-hover:!opacity-100"
                    title="Cambiar foto de perfil"
                  >
                    <EditRoundedIcon sx={{ fontSize: 30 }} className="text-[#272727]" />
                  </div>
                  <img src={user.profileimageurl || avatarPlaceholder} alt={user.fullname} className={`w-full h-full object-cover ${uploading ? "opacity-50" : ""}`} />
                  {uploading && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>

                {/* Indicator mobile */}
                <div className="absolute bottom-0 right-0 z-20 tablet:hidden">
                  <button
                    className="flex items-center justify-center rounded-full p-2 text-white focus:outline-none shadow-lg border-2 border-white"
                    onClick={handleAvatarClick}
                    title="Cambiar foto de perfil"
                    style={{ backgroundColor: theme.colors.backgroundColorCardBtn }}
                  >
                    <Camera className="size-5" />
                  </button>
                </div>

                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
              </div>

              <div className="flex flex-col gap-1">
                <h3 className="max-w-[85vw] truncate text-[30px] font-semibold">{user.fullname}</h3>
              </div>
            </section>

            {/* Personal Data Section */}
            <section className="flex flex-col gap-4">
              <h4 className="text-[24px] font-semibold">{lang?.UserProfile?.personalData || "Datos personales"}</h4>
              <div className="grid grid-flow-row">
                {/* Item: Nombre */}
                <div className="flex items-center justify-between py-3 border-b-2" style={{ borderColor: theme.colors.textColorNavbar + "66" }}>
                  <div className="flex flex-col">
                    <span className="text-[16px] font-medium">{lang?.UserProfile?.name || "Nombre"}</span>
                    <span className="text-[14px]">{user.fullname}</span>
                  </div>
                </div>

                {/* Item: Correo */}
                <div className="flex items-center justify-between py-3">
                  <div className="flex flex-col">
                    <span className="text-[16px] font-medium">{lang?.UserProfile?.emailLabel || "Correo electrónico"}</span>
                    <span className="text-[14px]">{user.email}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
      <Snackbar message={snackbar.message} severity={snackbar.severity} visible={snackbar.visible} />
    </>
  );
};
